<template>
    <div class="modal-overlay" @click="closeModal">
        <div class="modal-content" @click.stop>
            <div class="modal-header">
                <h2>{{ $t('הרשמה') }}</h2>
                <button @click="closeModal" class="close-btn">&times;</button>
            </div>
            
            <div class="modal-body">
                <form autocomplete="off" @submit.prevent="handleRegister">
                    <div class="form-group">
                        <label for="registerEmail">{{ $t('אימייל') }}:</label>
                        <input 
                            v-model="email" 
                            type="email" 
                            id="registerEmail"
                            required
                            :class="{ 'error': emailError }"
                        >
                        <p v-if="emailError" class="error-message">{{ emailError }}</p>
                    </div>

                    <div class="form-group">
                        <label for="registerPassword">{{ $t('סיסמא') }}:</label>
                        <input 
                            v-model="password" 
                            type="password" 
                            id="registerPassword"
                            required
                            :class="{ 'error': passwordError }"
                            autocomplete="new-password"
                        >
                        <p v-if="passwordError" class="error-message">{{ passwordError }}</p>
                    </div>

                    <div class="form-group">
                        <label for="confirmPassword">{{ $t('אישור סיסמא') }}:</label>
                        <input 
                            v-model="confirmPassword" 
                            type="password" 
                            id="confirmPassword"
                            required
                            :class="{ 'error': confirmPasswordError }"
                            autocomplete="new-password"
                        >
                        <p v-if="confirmPasswordError" class="error-message">{{ confirmPasswordError }}</p>
                    </div>

                    <div class="form-actions">
                        <button 
                            type="submit" 
                            class="register-submit-btn"
                            :disabled="isLoading || !isFormValid"
                        >
                            <span v-if="isLoading">{{ $t('נרשם...') }}</span>
                            <span v-else>{{ $t('הירשם') }}</span>
                        </button>
                        <button type="button" @click="closeModal" class="cancel-btn">
                            {{ $t('ביטול') }}
                        </button>
                    </div>

                    <div v-if="successMessage" class="success-message">
                        {{ successMessage }}
                    </div>
                    <div v-if="errorMessage" class="error-message">
                        {{ errorMessage }}
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import http from "@/http";

export default {
    name: 'RegisterModal',
    data() {
        return {
            email: '',
            password: '',
            confirmPassword: '',
            isLoading: false,
            successMessage: '',
            errorMessage: '',
            emailError: '',
            passwordError: '',
            confirmPasswordError: ''
        };
    },
    computed: {
        isEmailValid() {
            const pattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
            return pattern.test(this.email);
        },
        isPasswordValid() {
            return this.password.length >= 8;
        },
        isConfirmPasswordValid() {
            return this.password === this.confirmPassword;
        },
        isFormValid() {
            return this.isEmailValid && this.isPasswordValid && this.isConfirmPasswordValid;
        }
    },
    watch: {
        email() {
            this.validateEmail();
        },
        password() {
            this.validatePassword();
            this.validateConfirmPassword();
        },
        confirmPassword() {
            this.validateConfirmPassword();
        }
    },
    methods: {
        closeModal() {
            this.$emit('close');
        },
        validateEmail() {
            if (this.email && !this.isEmailValid) {
                this.emailError = this.$t('פורמט אימייל לא תקין');
            } else {
                this.emailError = '';
            }
        },
        validatePassword() {
            if (this.password && !this.isPasswordValid) {
                this.passwordError = this.$t('סיסמא חייבת להכיל לפחות 8 תווים');
            } else {
                this.passwordError = '';
            }
        },
        validateConfirmPassword() {
            if (this.confirmPassword && !this.isConfirmPasswordValid) {
                this.confirmPasswordError = this.$t('סיסמאות לא תואמות');
            } else {
                this.confirmPasswordError = '';
            }
        },
        async handleRegister() {
            this.clearMessages();
            
            if (!this.isFormValid) {
                this.validateEmail();
                this.validatePassword();
                this.validateConfirmPassword();
                return;
            }

            this.isLoading = true;

            try {
                const response = await http.post('register', {
                    email: this.email,
                    password: this.password
                });

                if (response.data.success) {
                    this.successMessage = this.$t('נרשמת בהצלחה, מייל הועבר למנהל פרוייקט לאישור.');
                    // Clear form after successful registration
                    this.email = '';
                    this.password = '';
                    this.confirmPassword = '';
                    
                    // Close modal after 3 seconds
                    setTimeout(() => {
                        this.closeModal();
                    }, 3000);
                } else {
                    this.errorMessage = this.$t('ההרשמה נכשלה. אנא נסה שוב.');
                }
            } catch (error) {
                console.error('Registration error:', error);
                if (error.response && error.response.data && error.response.data.message) {
                    this.errorMessage = error.response.data.message;
                } else {
                    this.errorMessage = this.$t('שגיאה בהרשמה. אנא נסה שוב מאוחר יותר.');
                }
            } finally {
                this.isLoading = false;
            }
        },
        clearMessages() {
            this.successMessage = '';
            this.errorMessage = '';
        }
    }
};
</script>

<style scoped lang="scss">
.modal-overlay {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 345px;
    max-width: 95vw;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background: black;
    color: white;
    border-radius: 12px;
    padding: 0;
    width: 90%;
    max-width: 500px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #eee;
    
    h2 {
        margin: 0;
        color: #fff;
        font-size: 1.5rem;
    }
}

.close-btn {
    background: none;
    border: none;
    font-size: 2rem;
    color: #fff;
    cursor: pointer;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
        color: #666;
    }
}

.modal-body {
    padding: 20px;
}

.form-group {
    margin-bottom: 20px;
    
    label {
        display: block;
        margin-bottom: 8px;
        font-weight: 600;
        color: #fff;
        text-align: right;
    }
    
    input {
        width: 100%;
        font-size: 16px;
        text-align: start;
        box-sizing: border-box;
        border-radius: 6.25rem;
        border: 1px solid var(--primary);
        background-color: transparent;
        height: 45px;
        color: #FFF;
        padding-inline: 0.8rem;
        min-width: 140px;
        
        &:focus {
            outline: none;
            border-color: var(--primary);
        }
        
        &.error {
            border-color: #dc3545;
        }
    }
}

.form-actions {
    display: flex;
    gap: 10px;
    justify-content: center;
    margin-top: 30px;
    
    button {
        padding: 12px 24px;
        border: none;
        border-radius: 8px;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        transition: background-color 0.3s;
        
        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }
}

.register-submit-btn {
    background-color: var(--primary);
    color: white;
}

.cancel-btn {
    background-color: #6c757d;
    color: white;
    
    &:hover {
        background-color: #545b62;
    }
}

.success-message {
    color: #28a745;
    background-color: #d4edda;
    border: 1px solid #c3e6cb;
    padding: 12px;
    border-radius: 8px;
    margin-top: 15px;
    text-align: center;
}

.error-message {
    color: #dc3545;
    padding: 8px;
    border-radius: 4px;
    margin-top: 8px;
    font-size: 14px;
    text-align: right;
}

// RTL support
.ltr {
    .form-group {
        label {
            text-align: left;
        }
        
        input {
            text-align: left;
        }
    }
    
    .error-message {
        text-align: left;
    }
}
</style>